import { useId, useState, type ReactNode } from 'react';
import {
  Badge,
  Icon,
  IconButton,
  IconRail,
  Modal,
  RailPanel,
  icons,
  theme,
} from '@agentisai/design-system';
import { useViewportTier } from '../hooks/useViewportTier';
import {
  hamburgerBar as hamburgerBarCls,
  main as mainCls,
  railSlot as railSlotCls,
  shell as shellCls,
} from './WorkspaceShell.css';

/** The five canonical rail panels. Only `workstreams` is openable in Drop 5. */
type PanelId = 'workstreams' | 'deliverables' | 'files' | 'agents' | 'memory';

interface RailEntry {
  readonly id: PanelId;
  readonly label: string;
  readonly icon: ReactNode;
  /** "Coming soon" placeholder buttons — focusable but `aria-disabled`. */
  readonly placeholder: boolean;
}

/**
 * Overlay a count badge on a rail glyph. A muted zero rather than a hidden
 * badge: "does this workstream have files?" is the question the rail is being
 * glanced at to answer, and a suppressed badge answers it only by absence —
 * which reads the same as "not loaded yet". `aria-hidden` because the count is
 * already in the button's accessible name.
 */
function withCountBadge(icon: ReactNode, count: number): ReactNode {
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      {icon}
      <Badge
        tone={count > 0 ? 'info' : 'neutral'}
        size="sm"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: `calc(-1 * ${theme.space['1_5']})`,
          insetInlineEnd: `calc(-1 * ${theme.space['2_5']})`,
          pointerEvents: 'none',
        }}
      >
        {count}
      </Badge>
    </span>
  );
}

/**
 * Static rail entries (icons + tooltip labels) per the DS `IconRail` story and
 * the agentis2 prototype 1:1. Only Workstreams is wired in Drop 5; the other
 * four are placeholders.
 */
const RAIL_ITEMS: readonly RailEntry[] = [
  {
    id: 'workstreams',
    label: 'Workstreams',
    icon: <Icon as={icons.LayoutGrid} size={20} />,
    placeholder: false,
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: <Icon as={icons.FileText} size={20} />,
    placeholder: true,
  },
  {
    // Files is live from Drop 6 (S08) — but only when a workstream is selected;
    // the id-based branch in the rail map handles its gated-vs-live states, so
    // it is not a "coming soon" placeholder.
    id: 'files',
    label: 'Files',
    icon: <Icon as={icons.Folder} size={20} />,
    placeholder: false,
  },
  {
    id: 'agents',
    label: 'Agents',
    icon: <Icon as={icons.Users} size={20} />,
    placeholder: true,
  },
  {
    id: 'memory',
    label: 'Workstream Memory',
    icon: <Icon as={icons.Lightbulb} size={20} />,
    placeholder: true,
  },
] as const;

export interface WorkspaceShellProps {
  /**
   * Main pane content — sits to the right of the rail + active panel.
   * The shell itself is layout chrome; the surface it frames (empty state,
   * artifact workspace, etc.) is the consumer's concern.
   */
  readonly children: ReactNode;
  /**
   * Rail-panel body — typically the workstream list/bubbles (C3). When the
   * Workstreams panel is collapsed this is rendered hidden (the panel mounts
   * once and toggles via `hidden`, so React Query state survives a collapse).
   */
  readonly workstreamsPanel?: ReactNode;
  /** Right-aligned action in the Workstreams subheader (e.g. `+ New`, S-C3). */
  readonly workstreamsAction?: ReactNode;
  /**
   * Files rail-panel body (Drop 6 / S09). Rendered inside the Files `RailPanel`
   * when the Files icon is opened — which is only reachable when a workstream is
   * selected. Mounted only while a workstream is selected so its per-workstream
   * query (S09) keys correctly and tears down on deselect.
   */
  readonly filesPanel?: ReactNode;
  /**
   * Number of files in the selected workstream, for the rail's count badge.
   * `null` while no workstream is selected or the count has not loaded — the
   * badge is suppressed rather than guessing zero, since "no workstream" and
   * "an empty workstream" are different states and only one of them is a fact.
   */
  readonly filesCount?: number | null;
  /** Right-aligned action in the Files subheader (the inert "+ New" pill, S10). */
  readonly filesAction?: ReactNode;
  /**
   * The currently-active workstream id (the `?ws=` selection, threaded from the
   * page). Gates the Files rail icon: absent → the icon is disabled with a
   * "select a workstream" tooltip; present → the icon opens the Files panel.
   */
  readonly selectedWorkstreamId?: string | null;
}

/**
 * WorkspaceShell — composes the DS `IconRail` + `RailPanel` into the real
 * authenticated workspace (S-C2).
 *
 * A11y model is the DS disclosure pattern (M11): the active rail button
 * carries `aria-expanded` + `aria-controls` pointing at the panel's id. The
 * three placeholder buttons (Deliverables / Agents / Workstream Memory) and the
 * gated Files button are NOT native-disabled — that would suppress pointer +
 * focus and hide their tooltip from keyboard + screen-reader users — they carry
 * `aria-disabled="true"`, stay focusable, and click is a no-op.
 *
 * Panel state is a single mutually-exclusive machine keyed by `PanelId`:
 * `Workstreams` is always live; `Files` becomes live once a workstream is
 * selected (Drop 6 / S08). Deselecting a workstream while Files is open collapses
 * it by derivation (no effect), never leaving an open-but-inert panel.
 */
export function WorkspaceShell({
  children,
  workstreamsPanel,
  workstreamsAction,
  filesPanel,
  filesCount = null,
  filesAction,
  selectedWorkstreamId,
}: WorkspaceShellProps) {
  // Mutually-exclusive panel state. Default open on Workstreams — the rail is
  // the primary surface; landing collapsed would hide the create flow C3 wires
  // into the panel.
  const [openPanel, setOpenPanel] = useState<PanelId | null>('workstreams');

  // Distinct stable ids per panel for `aria-controls` ↔ panel id wiring.
  const workstreamsPanelId = useId();
  const filesPanelId = useId();

  const filesEnabled = Boolean(selectedWorkstreamId);

  // Derived open-state (frontend-architecture R2 — computed in render, no
  // effect): Files can only be *shown* open when it is enabled, so deselecting
  // the workstream collapses an open Files panel by computation.
  function isPanelOpen(id: PanelId): boolean {
    if (id === 'files') return openPanel === 'files' && filesEnabled;
    return openPanel === id;
  }

  function toggle(id: PanelId) {
    setOpenPanel((prev) => (prev === id ? null : id));
  }

  // If the open panel just became disabled (workstream deselected while Files
  // was open), collapse it for real — React's sanctioned "adjust state when a
  // prop changes" pattern (a guarded setState during render, NOT an effect), so
  // a later re-select doesn't spring the Files panel back open unbidden. The
  // condition self-clears on the immediate re-render, so it never loops.
  if (openPanel === 'files' && !filesEnabled) {
    setOpenPanel(null);
  }

  const isOpen = isPanelOpen('workstreams');

  // S-C4 — compact-tier drawer state. The hamburger button (rendered only at
  // compact via CSS) opens the same workstreamsPanel content inside a Modal
  // using `RailPanel fluid` per the canonical recipe. The Modal's `useDialog`
  // handles focus trap + Escape + body scroll lock (DS contract).
  const tier = useViewportTier();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Derive (don't sync via useEffect → setState — react-hooks lint blocks
  // cascading renders): if the viewport flips to regular the desktop rail
  // owns the panel, so the centred drawer collapses by computation, not by
  // an effect that races the next render.
  const drawerVisible = drawerOpen && tier === 'compact';

  return (
    <div className={shellCls}>
      {/* Rail + panel hidden under `lg` (1024px) — compact-tier nav is C4 (PLAN).
          The slot stays in the DOM at all viewports so React Query / shell
          state doesn't tear when the viewport crosses the breakpoint mid-session.
          Without this, the 48px rail + 280px panel force horizontal overflow at
          375px and the S-F2 e2e fails. */}
      <div className={railSlotCls}>
        <IconRail>
          {RAIL_ITEMS.map((item) => {
            // Files (Drop 6 / S08) is gated: live only when a workstream is
            // selected. With none selected it reads like a placeholder — focusable
            // + aria-disabled + a "select a workstream" tooltip, click inert.
            if (item.id === 'files' && !filesEnabled) {
              return (
                <IconRail.Button
                  key={item.id}
                  aria-label="Files (select a workstream first)"
                  aria-disabled="true"
                  title="Select a workstream to view its files"
                  icon={item.icon}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                />
              );
            }
            if (item.placeholder) {
              // aria-disabled (not native `disabled`) so the button stays in the
              // tab order + receives focus + dispatches synthetic clicks; the
              // onClick is a no-op so the placeholder doesn't act. The native
              // `title` is the keyboard-reachable tooltip (lucide-react glyphs
              // already carry an aria role; rail label is the accessible name).
              return (
                <IconRail.Button
                  key={item.id}
                  aria-label={`${item.label} (coming soon)`}
                  aria-disabled="true"
                  title={`${item.label} — coming soon`}
                  icon={item.icon}
                  onClick={(e) => {
                    // Mirrors the spec's "remains focusable, click is inert"
                    // behaviour — never advance state from a placeholder.
                    e.preventDefault();
                  }}
                />
              );
            }
            // Live disclosures — Workstreams (always) + Files (with a workstream).
            const controls = item.id === 'files' ? filesPanelId : workstreamsPanelId;
            const showCount = item.id === 'files' && filesCount !== null;
            return (
              <IconRail.Button
                key={item.id}
                // The count rides in the accessible name, not the badge, so a
                // screen reader hears "Files, 3 files" once instead of the glyph
                // label and a loose number.
                aria-label={
                  showCount
                    ? `${item.label}, ${filesCount} ${filesCount === 1 ? 'file' : 'files'}`
                    : item.label
                }
                active={isPanelOpen(item.id)}
                aria-controls={controls}
                icon={showCount ? withCountBadge(item.icon, filesCount) : item.icon}
                onClick={() => toggle(item.id)}
              />
            );
          })}
        </IconRail>
        {/* Panel stays mounted so its useQuery (C1) keeps its cache across a
          collapse; `hidden` hides it from layout + the a11y tree at once. */}
        <div hidden={!isOpen}>
          <RailPanel
            id={workstreamsPanelId}
            title="Workstreams"
            subheaderLabel="MY WORKSTREAMS"
            subheaderAction={workstreamsAction}
          >
            {workstreamsPanel}
          </RailPanel>
        </div>
        {/* Files panel (Drop 6). Mounted only while a workstream is selected so
          its per-workstream query (S09) keys correctly and tears down on
          deselect; `hidden` toggles visibility on collapse without unmounting. */}
        {filesEnabled && (
          <div hidden={!isPanelOpen('files')}>
            <RailPanel
              id={filesPanelId}
              title="Files"
              subheaderLabel="Files"
              subheaderAction={filesAction}
            >
              {filesPanel}
            </RailPanel>
          </div>
        )}
      </div>
      <main className={mainCls}>
        {/* Compact-only hamburger — CSS hides it ≥md so it never appears
            alongside the desktop rail. Opens the fluid RailPanel drawer. */}
        <div className={hamburgerBarCls}>
          <IconButton
            icon={<Icon as={icons.Menu} />}
            aria-label="Open workstreams"
            aria-expanded={drawerVisible}
            size="sm"
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
          />
        </div>
        {children}
      </main>
      {/* The compact drawer. Modal owns focus trap / Escape / body scroll
          lock (useDialog), and `flushBody` lets RailPanel reach the dialog
          edges so its own header is the visible chrome (no duplicated
          "Workstreams" title from Modal). aria-label provides the dialog
          name since we suppress Modal's title. */}
      <Modal
        open={drawerVisible}
        onClose={() => setDrawerOpen(false)}
        aria-label="Workstreams"
        flushBody
      >
        <RailPanel
          title="Workstreams"
          subheaderLabel="MY WORKSTREAMS"
          subheaderAction={workstreamsAction}
          fluid
        >
          {workstreamsPanel}
        </RailPanel>
      </Modal>
    </div>
  );
}
WorkspaceShell.displayName = 'WorkspaceShell';
